/**
 * Detailed reports (rows per doctor / patient / sub-service) and the order timeline.
 * Periods are filtered on the order request date (YYYY-MM-DD, whole days).
 */
import { api } from './baseApi';

export interface ReportPeriod {
  date_from?: string;
  date_to?: string;
}

/** Shared per-row numbers. Money counts only completed / still-open orders, never cancelled ones. */
export interface ReportMetrics {
  total_orders: number;
  pending_orders: number;
  active_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  completion_rate: number | null;
  completed_value: number;
  open_value: number;
  avg_service_rating: number | null;
  avg_provider_rating: number | null;
  ratings_count: number;
  last_order_at: string | null;
}

export interface ReportSummary {
  total_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  completed_value: number;
  open_value: number;
  unassigned_orders: number;
  patients_with_orders?: number;
  orders_without_patient_file?: number;
}

export interface DoctorReportRow extends ReportMetrics {
  id: number;
  provider_type: 'SPECIALIST' | 'CONSULTANT';
  full_name: string;
  phone_number: string;
  specialization: string;
  service_areas: string[];
  is_active: boolean;
  overall_rating: number;
}

export interface PatientReportRow extends ReportMetrics {
  id: number;
  full_name: string;
  phone_number: string;
  has_account: boolean;
  lead_source: string;
  gender: string;
  age: number | null;
  services_used: number;
  /** Services used in the period, most ordered first */
  services: Array<{ name: string; name_ar: string; count: number; completed: number }>;
  providers: Array<{ name: string; count: number }>;
  areas: string[];
  first_order_at: string | null;
}

export interface ServiceReportRow extends ReportMetrics {
  id: number;
  name: string;
  name_ar: string;
  service_id: number;
  service_name: string;
  service_name_ar: string;
  parent_name: string | null;
  is_active: boolean;
  patients_count: number;
  providers_count: number;
}

/** One order with all of its information (orders details report) */
export interface OrderDetailsRow {
  id: number;
  order_number: string;
  order_type: string;
  status: string;
  created_at: string;
  created_source: 'DASHBOARD' | 'APP' | 'PROVIDER' | 'SYSTEM' | null;
  created_by: string;
  customer_name: string;
  customer_phone: string;
  beneficiary_type: string;
  patient_file: number | null;
  patient_name: string;
  patient_age: number | null;
  patient_gender: string;
  patient_phone: string;
  health_conditions: string;
  allergies: string;
  current_medications: string;
  special_notes: string;
  service_name: string;
  subservice_name: string;
  internal_subservice_name: string;
  purchase_type: string;
  rental_days: number | null;
  quantity: number | null;
  is_special_request: boolean;
  preferred_gender: string;
  preferred_age_range: string;
  preferred_language: string;
  provider_type: 'SPECIALIST' | 'CONSULTANT' | null;
  provider_name: string;
  provider_phone: string;
  scheduled_date: string | null;
  scheduled_time_from: string | null;
  scheduled_time_to: string | null;
  city: string;
  area: string;
  full_address: string;
  latitude: string | null;
  longitude: string | null;
  service_price: number;
  platform_fee: number;
  total_amount: number;
  payment_number: string;
  payment_method: string;
  payment_status: string;
  paid_at: string | null;
  cash_collected_by: string;
  accepted_at: string | null;
  accepted_by: string;
  started_at: string | null;
  completed_at: string | null;
  completed_by: string;
  cancelled_at: string | null;
  cancelled_by: string;
  cancellation_reason: string;
  service_rating: number | null;
  provider_rating: number | null;
  review_comment: string;
  admin_notes: string;
}

export interface OverviewKpis {
  total_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  open_orders: number;
  completion_rate: number | null;
  cancellation_rate: number | null;
  completed_value: number;
  avg_order_value: number | null;
  customers: number;
  new_customers: number;
  avg_service_rating: number | null;
  avg_provider_rating: number | null;
  ratings_count: number;
}

export interface OverviewReport {
  current: OverviewKpis;
  previous: OverviewKpis | null;
  previous_period: { date_from: string; date_to: string } | null;
  bucket: 'day' | 'week' | 'month';
  trend: Array<{ bucket: string; orders: number; completed: number; cancelled: number; completed_value: number }>;
  status_breakdown: Array<{ status: string; count: number }>;
}

export interface DurationStats {
  count: number;
  avg_hours: number | null;
  median_hours: number | null;
  distribution: { under_1h: number; '1_4h': number; '4_24h': number; over_24h: number };
}

export interface OperationsReport {
  time_to_accept: DurationStats;
  time_to_complete: DurationStats;
  orders_without_timeline: number;
  cancellations: {
    total: number;
    rate: number | null;
    reasons: Array<{ reason: string; count: number }>;
    by_source: Record<string, number>;
  };
  by_weekday: Array<{ weekday: number; orders: number }>;
  by_hour: Array<{ hour: number; orders: number }>;
  areas: Array<{ area: string; orders: number; completed: number; completed_value: number }>;
}

export interface CustomersReport {
  customers: number;
  new_accounts: number;
  repeat_customers: number;
  repeat_rate: number | null;
  new_vs_returning: Record<'new' | 'returning', { customers: number; orders: number; completed_value: number }>;
  has_comparison_base: boolean;
  lead_sources: Array<{ lead_source: string; patients: number; orders: number; completed_value: number }>;
  age_groups: Array<{ group: string; orders: number }>;
  genders: Array<{ gender: string; orders: number }>;
}

export interface MoneyReport {
  earned: number;
  service_value: number;
  delivery_fees: number;
  collected: number;
  collected_orders: number;
  uncollected: number;
  uncollected_orders: number;
  collected_by_method: Array<{ payment_method: string; count: number; amount: number }>;
}

export interface DetailedReport<Row> {
  summary: ReportSummary;
  rows: Row[];
}

export type TimelineEventType =
  | 'CREATED'
  | 'STATUS_CHANGED'
  | 'PROVIDER_ASSIGNED'
  | 'PROVIDER_CHANGED'
  | 'PROVIDER_REMOVED'
  | 'PAYMENT_STATUS';

export interface TimelineEvent {
  id: number | null;
  event_type: TimelineEventType;
  from_value: string;
  to_value: string;
  actor_id: number | null;
  actor_name: string;
  source: 'DASHBOARD' | 'APP' | 'PROVIDER' | 'SYSTEM' | null;
  note: string;
  created_at: string;
  /** Reconstructed for orders created before the timeline was recorded */
  is_historical: boolean;
}

export interface OrderTimeline {
  events: TimelineEvent[];
  has_untracked_history: boolean;
  summary: {
    requested_at: string;
    accepted_at: string | null;
    completed_at: string | null;
    seconds_to_accept: number | null;
    seconds_to_complete: number | null;
  };
}

export const detailedReportsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDoctorsReport: builder.query<
      { data: DetailedReport<DoctorReportRow> },
      ReportPeriod & { provider_type?: 'SPECIALIST' | 'CONSULTANT' }
    >({
      query: (params) => ({ url: '/dashboard/reports/doctors/', params }),
      providesTags: ['Statistics'],
    }),
    getPatientsReport: builder.query<{ data: DetailedReport<PatientReportRow> }, ReportPeriod>({
      query: (params) => ({ url: '/dashboard/reports/patients/', params }),
      providesTags: ['Statistics'],
    }),
    getServicePerformanceReport: builder.query<{ data: DetailedReport<ServiceReportRow> }, ReportPeriod>({
      query: (params) => ({ url: '/dashboard/reports/service-performance/', params }),
      providesTags: ['Statistics'],
    }),
    /** Patients report as an Excel workbook (patients, services per patient, orders) */
    exportPatientsReport: builder.query<Blob, ReportPeriod & { lang: string }>({
      query: (params) => ({
        url: '/dashboard/reports/patients/export/',
        params,
        responseHandler: (response) => response.blob(),
      }),
      keepUnusedDataFor: 0,
    }),
    /** One service's report as an Excel workbook (summary, orders, doctors, reviews, areas) */
    exportServiceReport: builder.query<Blob, ReportPeriod & { subservice_id: number; lang: string }>({
      query: ({ subservice_id, ...params }) => ({
        url: `/dashboard/reports/service-performance/${subservice_id}/export/`,
        params,
        responseHandler: (response) => response.blob(),
      }),
      keepUnusedDataFor: 0,
    }),
    /** All order information for the Orders page Excel export (same filters as the orders list) */
    getOrdersDetailsReport: builder.query<
      { data: DetailedReport<OrderDetailsRow> },
      ReportPeriod & { status?: string; search?: string; special_request?: boolean }
    >({
      query: (params) => ({ url: '/dashboard/reports/orders/details/', params }),
      providesTags: ['Statistics'],
    }),
    getOverviewReport: builder.query<{ data: OverviewReport }, ReportPeriod>({
      query: (params) => ({ url: '/dashboard/reports/overview/', params }),
      providesTags: ['Statistics'],
    }),
    getOperationsReport: builder.query<{ data: OperationsReport }, ReportPeriod>({
      query: (params) => ({ url: '/dashboard/reports/operations/', params }),
      providesTags: ['Statistics'],
    }),
    getCustomersReport: builder.query<{ data: CustomersReport }, ReportPeriod>({
      query: (params) => ({ url: '/dashboard/reports/customers/', params }),
      providesTags: ['Statistics'],
    }),
    getMoneyReport: builder.query<{ data: MoneyReport }, ReportPeriod>({
      query: (params) => ({ url: '/dashboard/reports/money/', params }),
      providesTags: ['Statistics'],
    }),
    getOrderTimeline: builder.query<{ data: OrderTimeline }, number>({
      query: (orderId) => `/dashboard/orders/${orderId}/timeline/`,
      // Refreshes whenever the order itself is refetched after a change
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
  }),
});

export const {
  useGetDoctorsReportQuery,
  useGetPatientsReportQuery,
  useGetServicePerformanceReportQuery,
  useLazyExportServiceReportQuery,
  useLazyExportPatientsReportQuery,
  useGetOrdersDetailsReportQuery,
  useLazyGetOrdersDetailsReportQuery,
  useGetOrderTimelineQuery,
  useGetOverviewReportQuery,
  useGetOperationsReportQuery,
  useGetCustomersReportQuery,
  useGetMoneyReportQuery,
} = detailedReportsApi;
